import {
  Component,
  Input,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core'
import * as L from 'leaflet'
import { RouteData } from '../../interfaces/route.interface'
import { CommonModule } from '@angular/common'

@Component({
  selector: 'app-route-map',
  templateUrl: './route-map.component.html',
  styleUrls: ['./route-map.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class RouteMapComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() routeData!: RouteData
  @Input() optimizedRouteData!: RouteData // Ajouter l'itinéraire optimisé
  @Input() interventions: any[] = []
  @Input() selectedUser: any // Ajouter l'utilisateur sélectionné pour récupérer son domicile
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef

  private map!: L.Map
  private routeLayer!: L.Polyline
  private optimizedRouteLayer!: L.Polyline // Nouvelle couche pour l'itinéraire optimisé
  private markers: L.Marker[] = []
  private interventionMarkers: L.Marker[] = []
  private homeMarker: L.Marker | null = null // Marqueur pour le domicile du technicien
  private legend: L.Control | null = null
  private loadingControl: L.Control | null = null // Contrôle pour l'indicateur de chargement
  private routeControlPanel: L.Control | null = null // Contrôle pour les boutons de visibilité

  public isLoading = false // État de chargement
  public showCurrentRoute = true // Visibilité de l'itinéraire actuel
  public showOptimizedRoute = true // Visibilité de l'itinéraire optimisé

  ngOnInit(): void {
    // Fix pour les icônes par défaut de Leaflet
    delete (L.Icon.Default.prototype as any)._getIconUrl
    L.Icon.Default.mergeOptions({
      iconRetinaUrl:
        'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
      iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
      shadowUrl:
        'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['routeData'] ||
        changes['optimizedRouteData'] ||
        changes['selectedUser']) &&
      this.map
    ) {
      console.log(this.selectedUser)
      this.displayRoute()
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initializeMap()
      if (this.routeData) {
        this.displayRoute()
      }
    }, 100)
  }

  private initializeMap(): void {
    this.map = L.map(this.mapContainer.nativeElement, {
      center: [43.6047, 1.4442],
      zoom: 10,
    })

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(this.map)
  }

  private displayRoute(): void {
    if (!this.map) {
      console.warn('Map not available')
      return
    }

    // Supprimer les anciens itinéraires et marqueurs s'ils existent
    if (this.routeLayer) {
      this.map.removeLayer(this.routeLayer)
    }
    if (this.optimizedRouteLayer) {
      this.map.removeLayer(this.optimizedRouteLayer)
    }
    this.markers.forEach((marker) => this.map.removeLayer(marker))
    this.interventionMarkers.forEach((marker) => this.map.removeLayer(marker))
    if (this.homeMarker) {
      this.map.removeLayer(this.homeMarker)
      this.homeMarker = null
    }
    this.markers = []
    this.interventionMarkers = []

    // Supprimer l'ancienne légende si elle existe
    if (this.legend) {
      this.map.removeControl(this.legend)
      this.legend = null
    }

    // Supprimer l'ancien panneau de contrôle s'il existe
    if (this.routeControlPanel) {
      this.map.removeControl(this.routeControlPanel)
      this.routeControlPanel = null
    }

    let allBounds: L.LatLngBounds | null = null

    // Afficher l'itinéraire normal si visible
    if (this.routeData?.geometry?.coordinates && this.showCurrentRoute) {
      const latLngs: L.LatLngExpression[] =
        this.routeData.geometry.coordinates.map((coord) => [coord[1], coord[0]])

      this.routeLayer = L.polyline(latLngs, {
        color: '#3388ff',
        weight: 4,
        opacity: 0.6,
        dashArray: '10, 10', // Ligne pointillée pour différencier
      }).addTo(this.map)

      allBounds = this.routeLayer.getBounds()
    }

    // Afficher l'itinéraire optimisé si visible
    if (
      this.optimizedRouteData?.geometry?.coordinates &&
      this.showOptimizedRoute
    ) {
      const optimizedLatLngs: L.LatLngExpression[] =
        this.optimizedRouteData.geometry.coordinates.map((coord) => [
          coord[1],
          coord[0],
        ])

      this.optimizedRouteLayer = L.polyline(optimizedLatLngs, {
        color: '#ff6b35',
        weight: 4,
        opacity: 0.8,
      }).addTo(this.map)

      if (allBounds) {
        allBounds.extend(this.optimizedRouteLayer.getBounds())
      } else {
        allBounds = this.optimizedRouteLayer.getBounds()
      }
    }

    // Ajouter les marqueurs des interventions
    this.addInterventionMarkers()

    // Ajouter le marqueur du domicile du technicien
    this.addHomeMarker()

    // Ajuster la vue pour inclure tous les itinéraires
    if (allBounds) {
      this.map.fitBounds(allBounds, { padding: [20, 20] })
    }

    // Ajouter le panneau de contrôle pour masquer/afficher les routes
    this.addRouteControlPanel()

    // Ajouter une légende seulement s'il y a des données à afficher
    if (this.routeData || this.optimizedRouteData) {
      this.addLegend()
    }
  }

  private addLegend(): void {
    this.legend = new L.Control({ position: 'bottomright' })
    this.legend.onAdd = () => {
      const div = L.DomUtil.create('div', 'route-legend')

      let legendContent =
        '<div style="background: white; padding: 10px; border-radius: 5px; box-shadow: 0 2px 5px rgba(0,0,0,0.2); font-family: Arial, sans-serif;">'
      legendContent +=
        '<h4 style="margin: 0 0 10px 0; font-size: 14px; color: #333;">Légende</h4>'

      if (this.routeData?.geometry?.coordinates) {
        legendContent +=
          '<div style="margin-bottom: 5px; display: flex; align-items: center;">'
        legendContent +=
          '<div style="width: 20px; height: 3px; background: #3388ff; opacity: 0.6; border: 1px dashed #3388ff; margin-right: 8px;"></div>'
        legendContent +=
          '<span style="font-size: 12px; color: #333;">Itinéraire actuel</span>'
        legendContent += '</div>'
      }

      if (this.optimizedRouteData?.geometry?.coordinates) {
        legendContent +=
          '<div style="margin-bottom: 5px; display: flex; align-items: center;">'
        legendContent +=
          '<div style="width: 20px; height: 3px; background: #ff6b35; margin-right: 8px;"></div>'
        legendContent +=
          '<span style="font-size: 12px; color: #333;">Itinéraire optimisé</span>'
        legendContent += '</div>'
      }

      if (this.interventions && this.interventions.length > 0) {
        legendContent +=
          '<div style="display: flex; align-items: center; margin-bottom: 5px;">'
        legendContent +=
          '<div style="width: 20px; height: 16px; background: #3388ff; border-radius: 4px; margin-right: 8px; color: white; font-size: 8px; display: flex; align-items: center; justify-content: center; font-weight: bold;">1</div>'
        legendContent +=
          '<span style="font-size: 12px; color: #333;">Intervention unique</span>'
        legendContent += '</div>'

        legendContent +=
          '<div style="display: flex; align-items: center; margin-bottom: 5px;">'
        legendContent +=
          '<div style="width: 20px; height: 20px; background: #ff6b35; border-radius: 50%; margin-right: 8px; color: white; font-size: 8px; display: flex; align-items: center; justify-content: center; font-weight: bold; position: relative;">2<div style="position: absolute; top: -2px; right: -2px; width: 6px; height: 6px; background: #dc3545; border-radius: 50%; border: 1px solid white;"></div></div>'
        legendContent +=
          '<span style="font-size: 12px; color: #333;">Interventions groupées</span>'
        legendContent += '</div>'
      }

      if (this.selectedUser?.homeLatitude && this.selectedUser?.homeLongitude) {
        legendContent += '<div style="display: flex; align-items: center;">'
        legendContent +=
          '<div style="width: 10px; height: 10px; background: #8B5CF6; border-radius: 50%; margin-right: 8px;"></div>'
        legendContent +=
          '<span style="font-size: 12px; color: #333;">Domicile</span>'
        legendContent += '</div>'
      }

      legendContent += '</div>'

      div.innerHTML = legendContent
      return div
    }

    this.legend.addTo(this.map)
  }

  private addInterventionMarkers(): void {
    if (!this.interventions || this.interventions.length === 0) return

    console.log('Adding intervention markers:', this.interventions)

    // Trier les interventions par heure de début pour avoir l'ordre correct
    const sortedInterventions = [...this.interventions].sort((a, b) => {
      const timeA = a.start ? new Date(a.start).getTime() : 0
      const timeB = b.start ? new Date(b.start).getTime() : 0
      return timeA - timeB
    })

    // Grouper les interventions par position géographique
    const interventionGroups = new Map<string, any[]>()
    const tolerance = 0.0001 // Tolérance pour considérer deux positions comme identiques

    sortedInterventions.forEach((intervention, index) => {
      if (
        intervention.data?.workSiteLatitude &&
        intervention.data?.workSiteLongitude
      ) {
        const lat = intervention.data.workSiteLatitude
        const lng = intervention.data.workSiteLongitude

        if (lat && lng) {
          // Chercher s'il existe déjà un groupe à cette position (avec tolérance)
          let groupKey = null
          for (const [key, group] of interventionGroups) {
            const [groupLat, groupLng] = key.split(',').map(Number)
            if (
              Math.abs(groupLat - lat) < tolerance &&
              Math.abs(groupLng - lng) < tolerance
            ) {
              groupKey = key
              break
            }
          }

          // Si aucun groupe trouvé, créer une nouvelle clé
          if (!groupKey) {
            groupKey = `${lat},${lng}`
            interventionGroups.set(groupKey, [])
          }

          // Ajouter l'intervention au groupe avec son index original
          interventionGroups
            .get(groupKey)!
            .push({ ...intervention, originalIndex: index })
        }
      }
    })

    // Créer les marqueurs pour chaque groupe
    interventionGroups.forEach((group, positionKey) => {
      const [lat, lng] = positionKey.split(',').map(Number)
      const interventionCount = group.length

      if (interventionCount === 1) {
        // Marqueur simple pour une seule intervention
        const intervention = group[0]
        const timeText = intervention.start
          ? new Date(intervention.start).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit',
            })
          : 'N/A'

        const interventionIcon = L.divIcon({
          html: `
            <div style="
              background-color: #3388ff;
              color: white;
              min-width: 40px;
              height: 35px;
              border-radius: 8px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              font-weight: bold;
              border: 2px solid white;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              text-align: center;
              padding: 2px;
            ">
              <div style="font-size: 12px; line-height: 1;">${intervention.originalIndex + 1}</div>
              <div style="font-size: 8px; line-height: 1; margin-top: 1px;">${timeText}</div>
            </div>
          `,
          className: 'custom-intervention-marker',
          iconSize: [40, 35],
          iconAnchor: [20, 35],
          popupAnchor: [0, -35],
        })

        const marker = L.marker([lat, lng], { icon: interventionIcon }).addTo(
          this.map
        ).bindPopup(`
            <div>
              <strong>#${intervention.originalIndex + 1} - ${intervention.title || 'Intervention'}</strong><br>
              <small>Heure: ${timeText}</small><br>
              <small>Adresse: ${intervention.data?.workSiteLocality || 'Non définie'}</small>
            </div>
          `)

        this.interventionMarkers.push(marker)
      } else {
        // Marqueur groupé pour plusieurs interventions
        const interventionIcon = L.divIcon({
          html: `
            <div style="
              background-color: #ff6b35;
              color: white;
              min-width: 45px;
              height: 40px;
              border-radius: 50%;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              font-size: 12px;
              font-weight: bold;
              border: 3px solid white;
              box-shadow: 0 3px 10px rgba(0,0,0,0.4);
              text-align: center;
              position: relative;
            ">
              <div style="font-size: 14px; line-height: 1;">${interventionCount}</div>
              <div style="font-size: 8px; line-height: 1; margin-top: 1px;">INT.</div>
              <div style="
                position: absolute;
                top: -5px;
                right: -5px;
                width: 12px;
                height: 12px;
                background-color: #dc3545;
                border-radius: 50%;
                border: 2px solid white;
                font-size: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
              ">!</div>
            </div>
          `,
          className: 'custom-intervention-cluster',
          iconSize: [45, 40],
          iconAnchor: [22, 40],
          popupAnchor: [0, -40],
        })

        // Créer le contenu du popup avec toutes les interventions
        let popupContent = `
          <div style="max-width: 300px;">
            <h4 style="margin: 0 0 10px 0; color: #333; font-size: 14px;">
              ${interventionCount} interventions à cette adresse
            </h4>
        `

        group.forEach((intervention) => {
          const timeText = intervention.start
            ? new Date(intervention.start).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
              })
            : 'N/A'

          popupContent += `
            <div style="
              border-left: 3px solid #3388ff;
              padding: 8px 12px;
              margin: 8px 0;
              background-color: #f8f9fa;
              border-radius: 4px;
            ">
              <strong>#${intervention.originalIndex + 1} - ${intervention.title || 'Intervention'}</strong><br>
              <small style="color: #666;">Heure: ${timeText}</small><br>
              <small style="color: #666;">Adresse: ${intervention.data?.workSiteLocality || 'Non définie'}</small>
            </div>
          `
        })

        popupContent += '</div>'

        const marker = L.marker([lat, lng], { icon: interventionIcon })
          .addTo(this.map)
          .bindPopup(popupContent)

        this.interventionMarkers.push(marker)
      }
    })
  }

  private addHomeMarker(): void {
    if (
      !this.selectedUser ||
      !this.selectedUser.homeLatitude ||
      !this.selectedUser.homeLongitude
    ) {
      return
    }

    // Icône personnalisée en forme de maison pour le domicile du technicien
    const homeIcon = L.divIcon({
      html: `
        <div style="
          background-color: #8B5CF6;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        ">
          🏠
        </div>
      `,
      className: 'custom-home-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -15],
    })

    const lat = this.selectedUser.homeLatitude
    const lng = this.selectedUser.homeLongitude

    this.homeMarker = L.marker([lat, lng], { icon: homeIcon }).addTo(this.map)
      .bindPopup(`
        <div>
          <strong>🏠 Domicile</strong><br>
          <small>${this.selectedUser.firstname} ${this.selectedUser.lastname}</small><br>
          <small>${this.selectedUser.locality || ''} ${this.selectedUser.postalCode || ''}</small>
        </div>
      `)
  }

  updateRoute(newRouteData: RouteData): void {
    this.routeData = newRouteData
    if (this.routeLayer) {
      this.map.removeLayer(this.routeLayer)
    }
    this.displayRoute()
  }

  public showLoading(): void {
    this.isLoading = true
    if (!this.map || this.loadingControl) return

    this.loadingControl = new L.Control({ position: 'topleft' })
    this.loadingControl.onAdd = () => {
      const div = L.DomUtil.create('div', 'route-loading')
      div.innerHTML = `
        <div style="
          background: rgba(255, 255, 255, 0.95);
          padding: 15px 20px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          font-family: Arial, sans-serif;
          display: flex;
          align-items: center;
          gap: 10px;
          border: 1px solid #e0e0e0;
        ">
          <div style="
            width: 20px;
            height: 20px;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #3388ff;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          "></div>
          <span style="
            color: #333;
            font-size: 14px;
            font-weight: 500;
          ">
            Calcul de l'itinéraire...
          </span>
        </div>
        <style>
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      `
      return div
    }

    this.loadingControl.addTo(this.map)
  }

  private addRouteControlPanel(): void {
    // Supprimer l'ancien panneau s'il existe
    if (this.routeControlPanel) {
      this.map.removeControl(this.routeControlPanel)
      this.routeControlPanel = null
    }

    // Ne pas afficher le panneau s'il n'y a pas de routes
    if (!this.routeData && !this.optimizedRouteData) {
      return
    }

    this.routeControlPanel = new L.Control({ position: 'topright' })
    this.routeControlPanel.onAdd = () => {
      const div = L.DomUtil.create('div', 'route-control-panel')

      let controlContent = `
        <div style="
          background: white;
          padding: 12px;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          font-family: Arial, sans-serif;
          border: 1px solid #e0e0e0;
          min-width: 180px;
        ">
          <h4 style="margin: 0 0 10px 0; font-size: 12px; color: #333; font-weight: 600;">
            Affichage des itinéraires
          </h4>
      `

      if (this.routeData?.geometry?.coordinates) {
        controlContent += `
          <div style="margin-bottom: 8px; display: flex; align-items: center;">
            <input type="checkbox" id="currentRoute" ${this.showCurrentRoute ? 'checked' : ''} 
                   style="margin-right: 8px; transform: scale(1.1);">
            <label for="currentRoute" style="font-size: 11px; color: #333; cursor: pointer; display: flex; align-items: center;">
              <div style="width: 15px; height: 2px; background: #3388ff; opacity: 0.6; border: 1px dashed #3388ff; margin-right: 6px;"></div>
              Itinéraire actuel
            </label>
          </div>
        `
      }

      if (this.optimizedRouteData?.geometry?.coordinates) {
        controlContent += `
          <div style="display: flex; align-items: center;">
            <input type="checkbox" id="optimizedRoute" ${this.showOptimizedRoute ? 'checked' : ''} 
                   style="margin-right: 8px; transform: scale(1.1);">
            <label for="optimizedRoute" style="font-size: 11px; color: #333; cursor: pointer; display: flex; align-items: center;">
              <div style="width: 15px; height: 2px; background: #ff6b35; margin-right: 6px;"></div>
              Itinéraire optimisé
            </label>
          </div>
        `
      }

      controlContent += '</div>'
      div.innerHTML = controlContent

      // Empêcher la propagation des événements de la carte
      L.DomEvent.disableClickPropagation(div)
      L.DomEvent.disableScrollPropagation(div)

      // Ajouter les écouteurs d'événements
      const currentRouteCheckbox = div.querySelector(
        '#currentRoute'
      ) as HTMLInputElement
      const optimizedRouteCheckbox = div.querySelector(
        '#optimizedRoute'
      ) as HTMLInputElement

      if (currentRouteCheckbox) {
        currentRouteCheckbox.addEventListener('change', () => {
          this.toggleCurrentRoute(currentRouteCheckbox.checked)
        })
      }

      if (optimizedRouteCheckbox) {
        optimizedRouteCheckbox.addEventListener('change', () => {
          this.toggleOptimizedRoute(optimizedRouteCheckbox.checked)
        })
      }

      return div
    }

    this.routeControlPanel.addTo(this.map)
  }

  public toggleCurrentRoute(show: boolean): void {
    this.showCurrentRoute = show
    if (this.routeLayer) {
      if (show) {
        this.routeLayer.addTo(this.map)
      } else {
        this.map.removeLayer(this.routeLayer)
      }
    }
  }

  public toggleOptimizedRoute(show: boolean): void {
    this.showOptimizedRoute = show
    if (this.optimizedRouteLayer) {
      if (show) {
        this.optimizedRouteLayer.addTo(this.map)
      } else {
        this.map.removeLayer(this.optimizedRouteLayer)
      }
    }
  }

  public hideLoading(): void {
    this.isLoading = false
    if (this.loadingControl && this.map) {
      this.map.removeControl(this.loadingControl)
      this.loadingControl = null
    }
  }
}
